import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Info, Building2, Layers } from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { CASES_DATA } from '../../data/siteData';
import { SEO } from '../../components/common/SEO';

export default function CaseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const caseItem = CASES_DATA.find((c) => c.slug === slug);

  if (!caseItem) {
    return <Navigate to="/cases" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-white text-navy">
      <SEO
        title={`${caseItem.title}｜事例・実績`}
        description={caseItem.summary}
        canonicalPath={`/cases/${caseItem.slug}`}
      />
      <Navbar />

      <main className="flex-1">
        {/* Header */}
        <section className="bg-hero-gradient border-b border-surface-border py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/cases"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-muted hover:text-spaq-cyan-dark mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>実績・事例・導入イメージ一覧へ戻る</span>
            </Link>

            <div className="flex flex-wrap items-center gap-2.5 mb-4">
              <Badge variant={caseItem.badgeColor}>{caseItem.category}</Badge>
              <span className="text-xs text-navy-muted">{caseItem.facilityType}</span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-navy leading-snug max-w-4xl">
              {caseItem.title}
            </h1>
            <p className="mt-3 text-base sm:text-lg text-spaq-cyan-dark font-medium">
              {caseItem.subtitle}
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              {/* Left 8 cols */}
              <div className="lg:col-span-8 space-y-8">
                {/* Summary Box */}
                <div className="rounded-2xl border border-surface-border bg-surface-slate p-6 sm:p-7">
                  <div className="text-xs font-bold text-spaq-cyan-dark uppercase tracking-wider mb-2">
                    OVERVIEW｜概要
                  </div>
                  <p className="text-sm sm:text-base text-navy leading-relaxed">
                    {caseItem.summary}
                  </p>
                </div>

                {/* Challenge & Solution */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="rounded-2xl border border-surface-border bg-white p-6 shadow-soft">
                    <div className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2">
                      CHALLENGE｜導入・検討前の課題
                    </div>
                    <p className="text-sm text-navy-muted leading-relaxed">
                      {caseItem.challenge}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-spaq-cyan/40 bg-surface-sky p-6 shadow-soft">
                    <div className="text-xs font-bold text-spaq-cyan-dark uppercase tracking-wider mb-2">
                      SOLUTION｜SPAQの設計・制御アプローチ
                    </div>
                    <p className="text-sm text-navy leading-relaxed">
                      {caseItem.solution}
                    </p>
                  </div>
                </div>

                {/* Detail Sections */}
                <div className="space-y-6 pt-2">
                  {caseItem.details.map((det, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl border border-surface-border bg-white p-6 sm:p-7"
                    >
                      <h2 className="text-lg font-bold text-navy mb-2.5">{det.heading}</h2>
                      <p className="text-sm text-navy-muted leading-relaxed">{det.body}</p>
                    </div>
                  ))}
                </div>

                {/* Condition & Integrity Note */}
                <div className="rounded-2xl bg-surface-warm border border-spaq-gold/30 p-5 flex items-start gap-3 text-xs text-navy-muted leading-relaxed">
                  <Info className="w-4 h-4 text-spaq-gold-dark shrink-0 mt-0.5" />
                  <div>{caseItem.conditionsNote}</div>
                </div>
              </div>

              {/* Right 4 cols: Meta & Outcomes */}
              <div className="lg:col-span-4 space-y-6">
                <div className="rounded-3xl border border-surface-border bg-surface-slate p-6 sm:p-7 shadow-soft space-y-5">
                  <div>
                    <div className="text-xs font-bold text-navy-muted mb-2 flex items-center gap-1.5">
                      <Building2 className="w-4 h-4 text-spaq-cyan-dark" />
                      <span>対象施設・規模条件</span>
                    </div>
                    <div className="text-sm font-semibold text-navy leading-relaxed">
                      {caseItem.facilityType}
                    </div>
                    <div className="text-xs text-navy-muted mt-1 leading-relaxed">
                      {caseItem.scaleInfo}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-surface-border">
                    <div className="text-xs font-bold text-navy-muted mb-2 flex items-center gap-1.5">
                      <Layers className="w-4 h-4 text-spaq-cyan-dark" />
                      <span>SPAQの担当・対象範囲</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {caseItem.scope.map((s, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2.5 py-1 rounded-lg bg-white border border-surface-border text-navy font-medium"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-surface-border space-y-3">
                    <div className="text-xs font-bold text-navy-muted">
                      主な効果・指標（{caseItem.category}）
                    </div>
                    {caseItem.outcomes.map((out, idx) => (
                      <div
                        key={idx}
                        className="bg-white rounded-2xl p-4 border border-surface-border"
                      >
                        <div className="text-xs text-navy-muted">{out.label}</div>
                        <div className="text-2xl font-bold text-spaq-cyan-dark mt-0.5">
                          {out.value}
                        </div>
                        {out.note && (
                          <div className="text-[11px] text-navy-muted mt-1">{out.note}</div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="pt-2">
                    <Button
                      to="/contact"
                      variant="primary"
                      className="w-full justify-center"
                      icon={<ArrowRight className="w-4 h-4" />}
                    >
                      同様の施設条件で相談する
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
